import React from "react";
import moment from "moment";

import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import { Button, Input, Label, Modal, ModalBody, ModalFooter } from "@/Components/ui";
import { ticketService } from "@/api/services/ticket.service";
import type { TicketProductSummary } from "@/api/types/ticket";
import {
  buildTicketProductPayload,
  findMissingTicketProductFields,
  type TicketExpirationUnit,
  type TicketProductDraft,
} from "@/pages/Tickets/TicketProducts/ticketProductForm.shared";
import { dialogService } from "@/utils/dialogService";
import { focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";
import { handleImageFiles } from "@/utils/handleImage";
import MyEditor from "../../../Components/editor/MyEditor";

interface AmazingPassTicketCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (ticket: TicketProductSummary) => void;
}

const createInitialDraft = (): TicketProductDraft => ({
  state: "어메이징패스",
  name: "",
  bundle: "1",
  price: "",
  membershipPrice: "",
  stock: "0",
  noLimit: true,
  canRefundEach: false,
  unit: "years",
  period: "1",
  targetDate: moment().add(365, "days").format("YYYY-MM-DD"),
  descript: "",
  usingInfo: "",
  thumbnail: [],
  detailImg: [],
});

const keepNonNegativeNumber = (value: string, fallback = ""): string => {
  if (!value) {
    return fallback;
  }

  return Number(value) < 0 ? fallback : value;
};

export default function AmazingPassTicketCreateModal({
  isOpen,
  onClose,
  onCreated,
}: AmazingPassTicketCreateModalProps) {
  const [draft, setDraft] = React.useState<TicketProductDraft>(createInitialDraft);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const resetDraft = React.useCallback(() => {
    setDraft(createInitialDraft());
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetDraft();
    }
  }, [isOpen, resetDraft]);

  const updateDraft = React.useCallback((patch: Partial<TicketProductDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const handleClose = React.useCallback(() => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }, [isSubmitting, onClose]);

  const handleSubmit = React.useCallback(async () => {
    const missingRequiredFields = findMissingTicketProductFields(draft, {
      name: "amazingpass-ticket-name",
      bundle: "amazingpass-ticket-bundle",
      price: "amazingpass-ticket-price",
      descript: "amazingpass-ticket-descript",
      usingInfo: "amazingpass-ticket-using-info",
    });

    if (missingRequiredFields.length > 0) {
      await dialogService.handleAlert(formatRequiredMessage(missingRequiredFields));
      focusFirstMissingField(missingRequiredFields);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await ticketService.createTicketProduct(
        buildTicketProductPayload(draft, { state: "어메이징패스" }),
      );
      const createdTicket = response.data;

      if (!createdTicket?.id || !createdTicket?.name) {
        throw new Error("생성된 티켓 정보를 확인할 수 없습니다.");
      }

      dialogService.notifySuccess("어메이징패스 티켓이 생성되었습니다.");
      onCreated({
        id: createdTicket.id,
        name: createdTicket.name,
        state: createdTicket.state ?? "어메이징패스",
        bundle_count: createdTicket.bundle_count,
      });
      resetDraft();
    } catch (error: any) {
      await dialogService.handleAlert(
        error?.response?.data?.message || error?.message || "티켓 생성에 실패했습니다.",
        { force: "error" },
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [draft, onCreated, resetDraft]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="어메이징패스 티켓 생성"
      variant="dialog"
      size="6xl"
      backdrop="static"
      scrollable
    >
      <ModalBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mb-3">
              <Label htmlFor="amazingpass-ticket-name" className="form-label">
                상품명
              </Label>
              <Input
                type="text"
                className="form-control"
                placeholder="상품명"
                id="amazingpass-ticket-name"
                value={draft.name}
                onChange={(e) => updateDraft({ name: e.target.value })}
                maxLength="100"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div>
            <div className="mb-3">
              <Label className="form-label">상태</Label>
              <Input type="text" className="form-control" value="어메이징패스" disabled />
            </div>
          </div>
          <div>
            <div className="mb-3">
              <Label htmlFor="amazingpass-ticket-bundle" className="form-label">
                티켓수
              </Label>
              <Input
                type="number"
                className="form-control"
                id="amazingpass-ticket-bundle"
                value="1"
                min="1"
                disabled
              />
              <p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>
                어메이징패스 티켓은 1개로 고정 생성됩니다.
              </p>
            </div>
          </div>
          <div>
            <div className="mb-3">
              <Label htmlFor="amazingpass-ticket-price" className="form-label">
                가격
              </Label>
              <Input
                type="number"
                className="form-control"
                placeholder="가격"
                id="amazingpass-ticket-price"
                value={draft.price}
                onChange={(e) => updateDraft({ price: keepNonNegativeNumber(e.target.value) })}
                min="0"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div>
            <div className="mb-3">
              <Label htmlFor="amazingpass-ticket-stock" className="form-label">
                재고
              </Label>
              <div className="mb-2 flex items-center gap-2">
                <Input
                  className="form-check-input mt-0"
                  type="checkbox"
                  id="amazingpass-ticket-no-limit"
                  checked={draft.noLimit}
                  onChange={(e) => updateDraft({ noLimit: e.target.checked, stock: "0" })}
                  disabled={isSubmitting}
                />
                <Label className="form-check-label mb-0" htmlFor="amazingpass-ticket-no-limit">
                  제한없음
                </Label>
              </div>
              <Input
                type="number"
                className="form-control"
                placeholder="재고"
                id="amazingpass-ticket-stock"
                value={draft.stock}
                onChange={(e) => updateDraft({ stock: keepNonNegativeNumber(e.target.value) })}
                min="0"
                disabled={draft.noLimit || isSubmitting}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
              <div>
                <Label htmlFor="amazingpass-ticket-unit" className="form-label">
                  유효기간
                </Label>
                <select
                  value={draft.unit}
                  onChange={(e) => updateDraft({ unit: e.target.value as TicketExpirationUnit })}
                  id="amazingpass-ticket-unit"
                  className="form-select"
                  disabled={isSubmitting}
                >
                  <option value="years">연</option>
                  <option value="months">월</option>
                  <option value="target_date">지정일</option>
                </select>
              </div>
              <div>
                <Label htmlFor="amazingpass-ticket-period" className="form-label">
                  적용값
                </Label>
                {draft.unit !== "target_date" ? (
                  <>
                    <Input
                      type="number"
                      className="form-control"
                      placeholder="유효기간"
                      id="amazingpass-ticket-period"
                      value={draft.period}
                      onChange={(e) => updateDraft({ period: keepNonNegativeNumber(e.target.value, "1") })}
                      min="1"
                      disabled={isSubmitting}
                    />
                    <p className="mt-2 mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "normal" }}>
                      연/월 입력은 티켓 발급일부터 계산되는 상대 기간입니다. 예: 1년 = 티켓 발급 후 1년 뒤 만료
                    </p>
                  </>
                ) : (
                  <CommonDatePicker
                    value={draft.targetDate}
                    onChange={(date) => updateDraft({ targetDate: moment(date[0]).format("YYYY-MM-DD") })}
                    className="form-control"
                    options={{
                      parseDate: (dateString) => moment(dateString).toDate(),
                      formatDate: (date) => moment(date).format("YYYY-MM-DD"),
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="mb-3">
              <Label htmlFor="amazingpass-ticket-thumbnail" className="form-label">
                대표이미지
              </Label>
              <Input
                className="form-control"
                type="file"
                id="amazingpass-ticket-thumbnail"
                accept="image/jpg, image/jpeg, image/png"
                onChange={(e) => handleImageFiles(e, (files) => updateDraft({ thumbnail: files }))}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div>
            <div className="mb-3">
              <Label htmlFor="amazingpass-ticket-detail-image" className="form-label">
                상세이미지
              </Label>
              <Input
                className="form-control"
                type="file"
                id="amazingpass-ticket-detail-image"
                accept="image/jpg, image/jpeg, image/png"
                multiple
                onChange={(e) => handleImageFiles(e, (files) => updateDraft({ detailImg: files }))}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="form-label">상세정보</Label>
            <div id="amazingpass-ticket-descript" tabIndex={-1} className="snow-editor" style={{ height: 260 }}>
              <MyEditor value={draft.descript} onChange={(value) => updateDraft({ descript: value })} uid={101} />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="form-label">이용안내</Label>
            <div id="amazingpass-ticket-using-info" tabIndex={-1} className="snow-editor" style={{ height: 260 }}>
              <MyEditor value={draft.usingInfo} onChange={(value) => updateDraft({ usingInfo: value })} uid={102} />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose} disabled={isSubmitting}>
          취소
        </Button>
        <Button color="success" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "생성 중..." : "생성 후 연결"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
